import { Component, ElementRef, OnDestroy, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Subject, Subscription } from 'rxjs';
import { catchError, flatMap, takeUntil, tap } from 'rxjs/operators';

import { PATH_NEW_MANAGER } from '../constants';
import { CompanySettings } from '../settings/model/company-settings';

import { LocaleUtils } from '../../localeselect/locale';

import * as fromCompanySettingsStore from '../settings/store';
import { DynamicTranslationService } from '../shared/dynamic-translation/dynamic-translation.service';
import { SessionService } from '../shared/session/session.service';
import { SideBarService } from '../shared/sidebar/sidebar.service';
import { AuthenticationService } from '../authentication/authentication.service';
import { environment } from 'environments/environment';
import { HttpClient } from '@angular/common/http';

@Component({
  selector: 'ls-login',
  templateUrl: './login.component.html',
  styleUrls: ['../../assets/stylesheets/auth.scss']
})
export class LoginComponent implements OnDestroy {
  @ViewChild('passwordInput') passwordInput: ElementRef;

  public error: string;
  public companyId: number;
  public username: string;
  public baseUrl: URL;
  public loginFrom: string;
  public ulp: string;
  public version: string;

  private redirectUrl: string = null;
  private ngUnsubscribe: Subject<Subscription> = new Subject();

  constructor(
    private session: SessionService,
    private authenticationService: AuthenticationService,
    private translationService: DynamicTranslationService,
    private httpClient: HttpClient,
    private route: ActivatedRoute,
    private router: Router,
    private sideBarService: SideBarService,
    private store: Store<fromCompanySettingsStore.CompanyState>
  ) {
    // only go to the login page if we are not using manage my restaurant
    if (!this.session.currentAppVersion) {
      location.href = `${location.origin.replace('-web', '')}${
        environment.authService
      }${window.location.hostname === 'localhost' ? '?local=true' : ''}`;
    }

    if (this.route.snapshot.paramMap.get('username')) {
      this.username = this.route.snapshot.paramMap.get('username');
    }
    if (this.route.snapshot.paramMap.get('companyId')) {
      this.companyId = Number(this.route.snapshot.paramMap.get('companyId'));
    }
    if (this.route.snapshot.paramMap.get('loginFrom')) {
      this.loginFrom = this.route.snapshot.paramMap.get('loginFrom');
    }
    if (this.route.snapshot.paramMap.get('page')) {
      // this.username = null;
      this.sideBarService.close();
      this.redirectUrl = this.route.snapshot.paramMap.get('page');
    }
    if (this.route.snapshot.paramMap.get('ulp')) {
      this.ulp = this.route.snapshot.paramMap.get('ulp');
    }

    if (this.route.snapshot.paramMap.get('version')) {
      this.version = this.route.snapshot.paramMap.get('version');
    }

    // If sign in through pos, we want to use the signout route to sign in,
    // since the user will keep staying signed in otherwise.
    // If ulp === false then we want to render the old sign in and not re-route
    if (this.loginFrom === 'pos' && this.ulp !== 'false') {
      // what to do if this.ulp is undefined?
      const redirectUrl = this.getRedirectUrl();

      this.httpClient
        .post<{ redirectUrl: string }>(
          `/PosServer/rest/internal/v1/sso/signout/manager`,
          null,
          { params: { redirect_uri: redirectUrl.href } }
        )
        .subscribe(response => {
          location.href = response.redirectUrl;
        });
    }
  }

  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  public login(credentials: any): void {
    this.authenticationService
      .login(credentials, this.companyId)
      .pipe(
        tap((token: string) => {
          this.session.createSession(token);
        }),
        flatMap(() =>
          fromCompanySettingsStore.getSettingsFromStore(this.store)
        ),
        tap((companySettings: CompanySettings) => {
          const languageCode: string = window.localStorage.getItem(
            'languageCode'
          );
          this.sideBarService.reset();
          if (companySettings.languageCode !== languageCode) {
            window.localStorage.setItem(
              'languageCode',
              companySettings.languageCode
            );
            window.location.href = `${PATH_NEW_MANAGER}${LocaleUtils.storageToUrlFormat(
              companySettings.languageCode
            )}/#`;
          }
          if (this.redirectUrl !== null) {
            const redirect: string =
              this.redirect(this.redirectUrl) || '/dashboard';
            this.redirectUrl = null;
            this.router.navigate([redirect]);
          }
        }),
        catchError(() => {
          this.passwordInput.nativeElement.value = '';
          return (this.error = this.translationService.translate(
            'Invalid username or password'
          ));
        }),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe();
  }

  private getRedirectUrl(): URL {
    const redirectUrl = new URL(
      `${location.origin.replace('-web', '')}${environment.authService}`
    );

    if (window.location.hostname === 'localhost') {
      redirectUrl.searchParams.append('localhost', 'true');
    }

    if (this.username) {
      redirectUrl.searchParams.append('username', this.username);
    }

    if (this.companyId) {
      redirectUrl.searchParams.append('companyId', this.companyId.toString());
    }

    if (this.loginFrom) {
      redirectUrl.searchParams.append('loginFrom', this.loginFrom);
    }

    if (this.version) {
      redirectUrl.searchParams.append('version', this.version);
    }

    return redirectUrl;
  }

  private redirect(url: string): string {
    const urlMapping: any = {
      'index.html': '/dashboard',
      './index.html': '/dashboard',
      'account.html': '/account',
      'products.html': '/products/product',
      './products.html': '/products/product',
      'login.html': '/login',
      'waiter.html': '/waiter',
      'waiter-mobile.html': '/waitermobile',
      'reports-mobile.html': '/reportmobile',
      'iphone.html': '/iphone',
      'devices.html': '/devices',
      './devices.html': '/devices',
      'devices_edit.html': '/devices/overview',
      'device_settings_edit.html': '/devices/restaurant-settings',
      './device_settings_edit.html': '/devices/restaurant-settings',
      'device_settings.html': '/devices/restaurant-settings',
      './support_dashboard.html': '/support/dashboard',
      'setupPayment.html': '/payments/processors',
      'resetpassword.html': '/reset',
      'company.html': '/settings/company',
      'payments.html': 'payments/types',
      '/reporting/advanced-reporting.html': '/reports/advanced/overview',
      '/reporting/advanced-reporting-disabled.html':
        '/reports/advanced/disabled',
      '/reporting/new.html': '/reports/advanced/new',
      '/reporting/report.html': '/reports/advanced/report'
    };
    return urlMapping[url];
  }
}
